import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  BookOpenIcon,
  UserIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ClockIcon,
  DocumentIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  AcademicCapIcon,
  PlayCircleIcon,
  VideoCameraIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../Common/LoadingSpinner';
import CourseContentViewer from './CourseContentViewer';
import toast from 'react-hot-toast';

const CourseDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [enrollmentLoading, setEnrollmentLoading] = useState(false);
  const [expandedModules, setExpandedModules] = useState([]);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    fetchCourseDetails();
    if (user?.role === 'student') {
      checkEnrollmentStatus();
    }
  }, [id, user]);

  const fetchCourseDetails = async () => {
    try {
      const response = await axios.get(`/api/courses/${id}`);
      console.log('Fetched course data:', response.data);
      setCourse(response.data);
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Course not found');
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };

  const checkEnrollmentStatus = async () => {
    try {
      const response = await axios.get(`/api/enrollments/student/${user._id}`);
      const enrolled = response.data.some(enrollment =>
        enrollment.course._id === id && enrollment.status === 'enrolled'
      );
      setIsEnrolled(enrolled);

      // Fetch progress if enrolled
      if (enrolled) {
        fetchProgress();
      }
    } catch (error) {
      console.error('Error checking enrollment:', error);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`/api/progress/course/${id}`);
      setProgress(response.data.progress);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const handleEnroll = async () => {
    try {
      setEnrollmentLoading(true);
      console.log('Enrolling in course:', id); // Debug log
      const response = await axios.post('/api/enrollments', { courseId: id });
      console.log('Enrollment response:', response.data); // Debug log

      // Initialize progress for the course
      const responseProgress = await axios.post(`/api/progress/initialize/${id}`);
      console.log('Progress initialization response:', responseProgress.data); // Debug log

      setIsEnrolled(true);
      toast.success('Successfully enrolled in course!');
      fetchCourseDetails(); // Refresh to update enrollment count
      fetchProgress(); // Fetch progress data
    } catch (error) {
      console.error('Enrollment error:', error); // Debug log
      const message = error.response?.data?.message || 'Failed to enroll';
      toast.error(message);
    } finally {
      setEnrollmentLoading(false);
    }
  };

  const handleUnenroll = async () => {
    try {
      // Find enrollment ID and delete
      const enrollments = await axios.get(`/api/enrollments/student/${user._id}`);

      const enrollment = enrollments.data.find(e => e.course._id === id);

      if (enrollment) {
        await axios.delete(`/api/enrollments/${enrollment._id}`);

        // Reset progress for the course
        await axios.delete(`/api/progress/course/${id}`);

        setIsEnrolled(false);
        setProgress(null);
        toast.success('Successfully unenrolled from course');
        fetchCourseDetails();
      }
    } catch (error) {
      toast.error('Failed to unenroll from course');
    }
  };

  const handleStartLearning = async () => {
    navigate(`/courses/${id}/learn`);
  }

  const handleApproveCourse = async () => {
    try {
      await axios.put(`/api/courses/${id}/approve`);
      toast.success('Course approved successfully!');
      fetchCourseDetails(); // Refresh course data
    } catch (error) {
      toast.error('Failed to approve course');
    }
  };

  const toggleModule = (moduleIndex) => {
    setExpandedModules(prev => {
      if (prev.includes(moduleIndex)) {
        return prev.filter(i => i !== moduleIndex);
      } else {
        return [...prev, moduleIndex];
      }
    });
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!course) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Course not found</h2>
      </div>
    );
  }

  const canEdit = user?.role === 'instructor' && course.instructor._id === user._id; // Removed admin from edit permissions
  const canApprove = user?.role === 'admin'; // Separate permission for approval

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Course Header */}
      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-4 mb-4">
              <div className="h-16 w-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <BookOpenIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{course.title}</h1>
                <p className="text-lg text-gray-600">{course.courseCode}</p>
              </div>
            </div>

            <p className="text-gray-700 mb-6">{course.description}</p>

            {/* Course Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center text-gray-600">
                <UserIcon className="h-5 w-5 mr-3" />
                <span>Instructor: {course.instructor.firstName} {course.instructor.lastName}</span>
              </div>

              <div className="flex items-center text-gray-600">
                <CalendarIcon className="h-5 w-5 mr-3" />
                <span>{course.credits} Credits • {course.level}</span>
              </div>

              <div className="flex items-center text-gray-600">
                <CurrencyDollarIcon className="h-5 w-5 mr-3" />
                <span>${course.fees}</span>
              </div>

              <div className="flex items-center text-gray-600">
                <ClockIcon className="h-5 w-5 mr-3" />
                <span>{course.currentEnrollment}/{course.maxStudents} enrolled</span>
              </div>
            </div>

            {/* Progress Stats */}
            {isEnrolled && progress && (
              <div className="mt-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-100 ">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Your Progress</h3>
                <div className="space-y-3">
                  {/* Overall Progress Bar */}
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-700 font-medium">Course Completion</span>
                      <span className="text-indigo-600 font-semibold">{progress.progressPercentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-indigo-500 to-purple-600 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${progress.progressPercentage}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Progress Details */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                    <div className="bg-white p-3 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-600 mb-1">Lectures Completed</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {progress.completedLecturesCount}/{progress.totalLecturesCount}
                      </p>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-600 mb-1">Modules</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {progress.modulesProgress.filter(m => m.completed).length}/{progress.modulesProgress.length}
                      </p>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-600 mb-1">Status</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {progress.completed ? (
                          <span className="text-green-600">Completed</span>
                        ) : (
                          <span className="text-indigo-600">In Progress</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>

          {/* Action Buttons */}
          <div className="mt-6 lg:mt-0 lg:ml-6 flex flex-col space-y-3">
            {canEdit && (
              <>
                <button
                  onClick={() => navigate(`/courses/${id}/edit`)}
                  className="btn btn-secondary flex items-center justify-center"
                >
                  <PencilIcon className="h-5 w-5 mr-2" />
                  Edit Course
                </button>
                <button
                  onClick={() => navigate(`/courses/${id}/materials`)}
                  className="btn btn-primary flex items-center justify-center"
                >
                  <DocumentIcon className="h-5 w-5 mr-2" />
                  Manage Materials
                </button>
              </>
            )}

            {canApprove && !course.isApproved && (
              <button
                onClick={handleApproveCourse}
                className="btn btn-primary flex items-center justify-center"
              >
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                Approve Course
              </button>
            )}

            {canApprove && (
              <button
                className="btn btn-danger flex items-center justify-center"
              >
                <TrashIcon className="h-5 w-5 mr-2" />
                Deactivate Course
              </button>
            )}

            {user?.role === 'student' && (
              <>
                {!isEnrolled ? (
                  <button
                    onClick={handleEnroll}
                    disabled={enrollmentLoading || course.currentEnrollment >= course.maxStudents || !course.isApproved}
                    className="btn btn-primary disabled:opacity-50"
                  >
                    {enrollmentLoading ? 'Enrolling...' :
                      !course.isApproved ? 'Pending Approval' :
                        course.currentEnrollment >= course.maxStudents ? 'Course Full' : 'Enroll Now'}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleStartLearning}
                      className="btn btn-primary"
                    >
                      {progress && progress.completedLecturesCount > 0 ? 'Continue Learning' : 'Start Learning'}
                    </button>

                    <button
                      onClick={handleUnenroll}
                      className="btn btn-danger"
                    >
                      Unenroll
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Course Materials - Now visible to all users */}
      <CourseContentViewer
        materials={course.materials || []}
        isEnrolled={isEnrolled}
        canEdit={canEdit}
        onEnroll={handleEnroll}
        enrollmentLoading={enrollmentLoading}
      />

      {/* Course Modules and Lectures */}
      {course.modules && course.modules.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Course Curriculum</h2>
            <div className="text-sm text-gray-600">
              {course.modules.length} {course.modules.length === 1 ? 'Module' : 'Modules'}
            </div>
          </div>

          <div className="space-y-4">
            {course.modules.map((module, moduleIndex) => (
              <div key={module._id} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Module Header */}
                <div className="bg-gray-50 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 cursor-pointer" onClick={() => toggleModule(moduleIndex)}>
                      <button
                        type="button"
                        className="text-gray-600 hover:text-gray-900 focus:outline-none"
                      >
                        {expandedModules.includes(moduleIndex) ? (
                          <ChevronUpIcon className="h-5 w-5" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5" />
                        )}
                      </button>
                      <AcademicCapIcon className="h-5 w-5 text-indigo-600" />
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">
                          Module {moduleIndex + 1}: {module.title}
                        </h3>
                        {module.description && (
                          <p className="text-sm text-gray-600 mt-1">{module.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 ml-4">
                      {module.lectures?.length || 0} {module.lectures?.length === 1 ? 'Lecture' : 'Lectures'}
                    </div>
                  </div>
                </div>

                {/* Module Content (Lectures) */}
                {expandedModules.includes(moduleIndex) && (
                  <div className="p-4 bg-white">
                    {module.lectures && module.lectures.length > 0 ? (
                      <div className="space-y-2">
                        {module.lectures.map((lecture, lectureIndex) => (
                          <div
                            key={lecture._id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center space-x-3 flex-1">
                              {lecture.type === 'video' ? (
                                <PlayCircleIcon className="h-5 w-5 text-indigo-600 flex-shrink-0" />
                              ) : (
                                <VideoCameraIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                              )}
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">{lecture.title}</p>
                                <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                                  <span>{formatDuration(lecture.duration)}</span>
                                  <span>•</span>
                                  <span>
                                    {lecture.type === 'video' ? (
                                      lecture.videoSource === 'upload' ? 'Video' : 'YouTube'
                                    ) : (
                                      `Live Session (${lecture.liveType})`
                                    )}
                                  </span>
                                  {lecture.isFreePreview && (
                                    <>
                                      <span>•</span>
                                      <span className="text-green-600 font-medium">Free Preview</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            {lecture.isFreePreview && (
                              <button
                                className="text-sm text-indigo-600 hover:text-indigo-800 font-medium ml-4"
                                onClick={() => {
                                  if (lecture.videoUrl) {
                                    window.open(lecture.videoUrl, '_blank');
                                  } else {
                                    toast.error('Video not available');
                                  }
                                }}
                              >
                                Preview
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4">No lectures added yet</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Prerequisites */}
      {course.prerequisites?.length > 0 && (
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Prerequisites</h2>
          <ul className="list-disc list-inside space-y-1">
            {course.prerequisites.map((prereq, index) => (
              <li key={index} className="text-gray-700">{prereq}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CourseDetail;
